import React, { useMemo } from 'react';

const DAYS  = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];


function fmt12(h) {
  if (h === 0)  return '12am';
  if (h === 12) return '12pm';
  return h < 12 ? `${h}am` : `${h - 12}pm`;
}

export default function PeakHoursChart({ orders = [] }) {
  // ── hourly totals (bar chart) ──────────────────────────────────────────────
  const hourlyData = useMemo(() => {
    const counts = Array(24).fill(0);
    orders.forEach(o => {
      if (!o.timestamp) return;
      const hour = new Date(o.timestamp * 1000).getHours();
      counts[hour]++;
    });
    const max = Math.max(...counts, 1);
    return counts.map((count, hour) => ({ hour, count, pct: (count / max) * 100 }));
  }, [orders]);

  // ── day-of-week totals ─────────────────────────────────────────────────────
  const dayData = useMemo(() => {
    const counts = Array(7).fill(0);
    orders.forEach(o => {
      if (!o.timestamp) return;
      counts[new Date(o.timestamp * 1000).getDay()]++;
    });
    const max = Math.max(...counts, 1);
    return counts.map((count, day) => ({ day: DAYS[day], count, pct: (count / max) * 100 }));
  }, [orders]);

  const peakHour = hourlyData.reduce((a, b) => (a.count >= b.count ? a : b), hourlyData[0]);
  const peakDay  = dayData.reduce((a, b) => (a.count >= b.count ? a : b), dayData[0]);

  const barColor = (pct) => {
    if (pct >= 80) return '#00ff99';
    if (pct >= 50) return '#00bfff';
    if (pct >= 20) return '#7b61ff';
    return '#2a2a4a';
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2 className="section-title">
          <span className="section-title-icon">🕐</span>
          Peak Hours Analysis
        </h2>
        <div style={{ display: 'flex', gap: '1rem' }}>
          <span style={{ fontSize: '0.8rem', color: '#aaa' }}>
            🔥 Busiest hour: <strong style={{ color: '#00ff99' }}>{fmt12(peakHour.hour)}</strong>
            {' '}({peakHour.count} sales)
          </span>
          <span style={{ fontSize: '0.8rem', color: '#aaa' }}>
            📅 Busiest day: <strong style={{ color: '#00bfff' }}>{peakDay.day}</strong>
            {' '}({peakDay.count} sales)
          </span>
        </div>
      </div>

      {/* ── Hourly bar chart ─────────────────────────────────────────────── */}
      <p style={{ color: '#555', fontSize: '0.75rem', margin: '0 0 0.75rem' }}>
        Sales by hour of day (all time)
      </p>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height: '100px', marginBottom: '0.5rem' }}>
        {hourlyData.map(({ hour, count, pct }) => (
          <div
            key={hour}
            style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}
            title={`${fmt12(hour)}: ${count} sales`}
          >
            <div style={{
              width: '100%',
              height: `${Math.max(pct, 2)}%`,
              background: barColor(pct),
              borderRadius: '3px 3px 0 0',
              opacity: pct < 5 ? 0.3 : 1,
              transition: 'height 0.3s ease',
              cursor: 'default',
            }} />
          </div>
        ))}
      </div>
      {/* Hour labels — show only every 3 hours */}
      <div style={{ display: 'flex', gap: '3px', marginBottom: '1.5rem' }}>
        {hourlyData.map(({ hour }) => (
          <div key={hour} style={{
            flex: 1, textAlign: 'center', fontSize: '0.6rem', color: '#555',
            overflow: 'hidden', whiteSpace: 'nowrap',
          }}>
            {hour % 3 === 0 ? fmt12(hour) : ''}
          </div>
        ))}
      </div>

      {/* ── Day-of-week bars ─────────────────────────────────────────────── */}
      <p style={{ color: '#555', fontSize: '0.75rem', margin: '0 0 0.75rem' }}>
        Sales by day of week (all time)
      </p>
      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', height: '72px', marginBottom: '0.5rem' }}>
        {dayData.map(({ day, count, pct }) => (
          <div key={day} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}
            title={`${day}: ${count} sales`}>
            <div style={{
              width: '100%', height: `${Math.max(pct, 3)}%`,
              background: barColor(pct),
              borderRadius: '4px 4px 0 0',
              opacity: pct < 5 ? 0.3 : 1,
            }} />
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: '8px' }}>
        {dayData.map(({ day, count }) => (
          <div key={day} style={{ flex: 1, textAlign: 'center', fontSize: '0.7rem', color: '#777' }}>
            <div>{day}</div>
            <div style={{ color: '#444', fontSize: '0.65rem' }}>{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
